import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

/** The page sizes a list offers (decision 182, U6) and the default. */
export const PAGE_SIZES = [10, 25, 50] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
export const DEFAULT_PAGE_SIZE: PageSize = 50;

/** Reads `pageSize` from the URL; anything but 10, 25 or 50 falls back to the default. */
export function parsePageSize(raw: string | null): PageSize {
  return PAGE_SIZES.find((size) => String(size) === raw) ?? DEFAULT_PAGE_SIZE;
}

/**
 * A list's search, filters and order, kept in the URL (decision 101, #401), as `PeoplePage` did
 * first: back and forward work, and a link shares the view. Writes replace the history entry, so
 * typing a search does not leave a trail of half-words behind «Indietro».
 *
 * «Azzera i filtri» clears the search and the filters, never the order or the page size: neither is
 * a filter. `pageSize` (10/25/50, default 50), `sort`, and `page` are not filter keys.
 * With `paged` enabled, changing search, filters, sort or size resets to page one.
 */
export function useListParams(filterKeys: readonly string[], paged = false) {
  const [params, setParams] = useSearchParams();
  // A stable key for the effect dependencies below; callers usually pass a literal array.
  const keys = filterKeys.join('\u0000');

  const set = useCallback(
    (key: string, value: string | null) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (paged && key !== 'page') next.delete('page');
          if (value === null || value === '') {
            next.delete(key);
          } else {
            next.set(key, value);
          }
          return next;
        },
        { replace: true },
      ),
    [setParams, paged],
  );

  const clear = useCallback(
    () =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete('q');
          if (paged) next.delete('page');
          for (const key of keys.split('\u0000')) {
            next.delete(key);
          }
          return next;
        },
        { replace: true },
      ),
    [setParams, keys, paged],
  );

  const active = useMemo(() => {
    const out: Record<string, string | null> = {};
    for (const key of keys.split('\u0000')) {
      out[key] = params.get(key);
    }
    return out;
  }, [params, keys]);

  const setPageSize = useCallback(
    (size: PageSize) => set('pageSize', size === DEFAULT_PAGE_SIZE ? null : String(size)),
    [set],
  );

  return {
    params,
    page: parsePage(params.get('page')),
    setPage: (page: number) => set('page', page <= 1 ? null : String(page)),
    active,
    q: params.get('q') ?? '',
    pageSize: parsePageSize(params.get('pageSize')),
    sort: params.get('sort') ?? '',
    set,
    setPageSize,
    clear,
  } as const;
}

export function parsePage(raw: string | null): number {
  const value = Number(raw);
  return Number.isSafeInteger(value) && value >= 1 && value <= 42949672 ? value : 1;
}
